import Swal, { SweetAlertOptions } from 'sweetalert2';

const TOP_BANNER_POSITIONS = new Set(['top', 'top-start', 'top-end']);
const SWIPE_THRESHOLD_PX = 60;
const MAX_VERTICAL_DRIFT_PX = 40;

let isPatched = false;

function isTopBannerToast(options: SweetAlertOptions): boolean {
  return options.toast === true && !!options.position && TOP_BANNER_POSITIONS.has(options.position);
}

function withSwipeToDismiss(options: SweetAlertOptions): SweetAlertOptions {
  const existingDidOpen = options.didOpen;

  return {
    ...options,
    didOpen: (popup: HTMLElement) => {
      existingDidOpen?.(popup);

      let startX = 0;
      let startY = 0;
      let currentDeltaX = 0;

      const onTouchStart = (event: TouchEvent): void => {
        if (!event.touches?.length) {
          return;
        }

        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        currentDeltaX = 0;
        popup.style.transition = 'none';
      };

      const onTouchMove = (event: TouchEvent): void => {
        if (!event.touches?.length) {
          return;
        }

        const touch = event.touches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = Math.abs(touch.clientY - startY);

        if (deltaY > MAX_VERTICAL_DRIFT_PX) {
          return;
        }

        currentDeltaX = deltaX;
        popup.style.transform = `translateX(${deltaX}px)`;
        popup.style.opacity = `${Math.max(0.35, 1 - Math.abs(deltaX) / 220)}`;
      };

      const resetPopup = (): void => {
        popup.style.transition = 'transform 0.18s ease, opacity 0.18s ease';
        popup.style.transform = 'translateX(0)';
        popup.style.opacity = '1';
      };

      const onTouchEnd = (): void => {
        if (Math.abs(currentDeltaX) >= SWIPE_THRESHOLD_PX) {
          void Swal.close();
          return;
        }

        resetPopup();
      };

      const onClick = (): void => {
        void Swal.close();
      };

      popup.addEventListener('touchstart', onTouchStart, { passive: true });
      popup.addEventListener('touchmove', onTouchMove, { passive: true });
      popup.addEventListener('touchend', onTouchEnd, { passive: true });
      popup.addEventListener('touchcancel', resetPopup, { passive: true });
      popup.addEventListener('click', onClick);
    },
  };
}

export function enableTopBannerSwalSwipeDismiss(): void {
  if (isPatched) {
    return;
  }

  isPatched = true;
  const originalFire = Swal.fire.bind(Swal);

  Swal.fire = ((...args: Parameters<typeof Swal.fire>) => {
    if (args.length > 0 && typeof args[0] === 'object' && args[0] !== null) {
      const options = args[0] as SweetAlertOptions;

      // Preserve full dialogs that explicitly opt out of the banner layout.
      if (options.toast === undefined) options.toast = true;

      if (isTopBannerToast(options)) {
        return originalFire(withSwipeToDismiss(options) as any);
      }

      return originalFire(options as any);
    }

    return originalFire(...args as any);
  }) as typeof Swal.fire;
}
